import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, Space, Spin, Table } from 'antd';
import { DashboardPageShell, DashboardPanel, DashboardPrimaryActionButton, DashboardRefreshButton, DashboardSearchInput, renderMaintTag, renderServiceTag } from '../components/dashboard';
import { MaintenanceStatusTag, ServiceStatusTag } from '../components/maintenance/MaintenanceStatusTags';
import { api } from '../api/http';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import DraftsPage from './DraftsPage';
import './forkliftsPages.css';

function valueOrBlank(value) {
  return value || value === 0 ? value : '';
}

function joinParts(parts) {
  return parts.map(valueOrBlank).filter(Boolean).join(' ');
}

function normalizeForklift(raw) {
  if (!raw) return null;
  return {
    id:
      raw.id ??
      raw.forkliftId ??
      raw.ForkliftId ??
      raw.ForkliftID ??
      raw.forklift_id ??
      null,
    unitId:
      raw.unitId ??
      raw.unit_id ??
      raw.unitID ??
      raw.UnitId ??
      raw.UnitID ??
      raw.forkliftUnitId ??
      null,
    make: raw.make ?? raw.Make ?? null,
    model: raw.model ?? raw.Model ?? null,
    serialNumber:
      raw.serialNumber ??
      raw.serial ??
      raw.SerialNumber ??
      raw.serial_number ??
      raw.Serial_Number ??
      null,
    hours:
      raw.hours ??
      raw.hourMeter ??
      raw.hour_meter ??
      raw.Hours ??
      raw.HourMeter ??
      raw.Hour_Meter ??
      null,
    serviceStatus:
      raw.serviceStatus ??
      raw.service_status ??
      raw.ServiceStatus ??
      raw.service ??
      raw.Service ??
      raw.status ??
      raw.Status ??
      null,
    maintenanceStatus:
      raw.maintenanceStatus ??
      raw.maintenance_status ??
      raw.MaintenanceStatus ??
      raw.maintenance ??
      raw.Maintenance ??
      null,
    customerId:
      raw.customerId ??
      raw.customer_id ??
      raw.CustomerId ??
      raw.CustomerID ??
      null,
  };
}

async function fetchForklifts() {
  try {
    const res = await api.get('/api/forklifts');
    const list = Array.isArray(res.data) ? res.data : [];
    return list.map((f) => normalizeForklift(f)).filter(Boolean);
  } catch {
    return [];
  }
}

export default function TechnicianForkliftsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const [activeTab, setActiveTab] = useState('FORKLIFTS');

  const [forklifts, setForklifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedForkliftId, setSelectedForkliftId] = useState(null);
  const [search, setSearch] = useState('');

  const headerTitle = resolveActiveCustomerContext(session).customerName;

  const loadForklifts = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchForklifts();
      const active = all.filter(
        (f) => String(f.serviceStatus || '').toUpperCase() !== 'RETIRED'
      );
      const sorted = [...active].sort((a, b) =>
        String(a.unitId || '').localeCompare(String(b.unitId || ''), undefined, {
          numeric: true,
        })
      );
      setForklifts(sorted);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadForklifts();
  }, [loadForklifts]);

  const rowClassName = (record) =>
    record.id === selectedForkliftId ? 'ant-table-row-selected' : '';

  const filteredForklifts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forklifts;
    return forklifts.filter((f) => {
      const unitId = (f.unitId || '').toLowerCase();
      const make = (f.make || '').toLowerCase();
      const model = (f.model || '').toLowerCase();
      const serial = (f.serialNumber || '').toLowerCase();
      return (
        unitId.includes(q) ||
        make.includes(q) ||
        model.includes(q) ||
        serial.includes(q)
      );
    });
  }, [forklifts, search]);

  function handleStartMaintenance(record) {
    if (!record || !record.unitId) return;
    const cid =
      record.customerId ?? record.customer_id ?? record.customerIdRef ?? null;
    if (cid) {
      try {
        localStorage.setItem('selectedCustomerId', String(cid));
      } catch {}
    }
    navigate(`/maintenance/${encodeURIComponent(record.unitId)}`);
  }

  function handleStartRepair(record) {
    if (!record || !record.unitId) return;
    const cid =
      record.customerId ?? record.customer_id ?? record.customerIdRef ?? null;
    if (cid) {
      try {
        localStorage.setItem('selectedCustomerId', String(cid));
      } catch {}
    }
    navigate(`/repairs/${encodeURIComponent(record.unitId)}`);
  }

  const columns = [
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
      render: (text) => (text ? <span>{text}</span> : ''),
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Serial #',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (value) => (value || value === 0 ? value : ''),
    },
    {
      title: 'Service',
      dataIndex: 'serviceStatus',
      key: 'serviceStatus',
      render: (status) => renderServiceTag(status),
    },
    {
      title: 'Maintenance',
      dataIndex: 'maintenanceStatus',
      key: 'maintenanceStatus',
      render: (status) => renderMaintTag(status),
    },
    {
      title: 'Maintenance Form',
      key: 'maintenanceForm',
      width: 160,
      render: (_, record) => (
        <DashboardPrimaryActionButton
          className="forklifts-page-table-action"
          type="primary"
          onClick={() => handleStartMaintenance(record)}
        >
          Maintenance
        </DashboardPrimaryActionButton>
      ),
    },
    {
      title: 'Repair',
      key: 'repair',
      render: (_, record) => (
        <Button
          className="forklifts-page-table-action technician-forklifts-page-repair-action"
          onClick={() => handleStartRepair(record)}
        >
          Repair
        </Button>
      ),
    },
  ];

  return (
    <DashboardPageShell title={headerTitle} productDashboardShell="customer" shellClassName="forklifts-page-shell technician-forklifts-page-shell">
      <Spin spinning={loading}>
        <DashboardPanel className="forklifts-page-panel">
          <header className="forklifts-page-heading technician-forklifts-page-heading">
            <div>
              <h1>Forklifts</h1>
              <p>{activeTab === 'DRAFTS' ? 'Saved maintenance and repair drafts will appear here.' : 'Start a maintenance or repair form from the list.'}</p>
            </div>
            <div className="maintenance-tabs-wrap">
              <div className="fleet-toggle maintenance-tabs" role="tablist" aria-label="Technician forklift sections">
                <button type="button" role="tab" aria-selected={activeTab === 'FORKLIFTS'} className="fleet-toggle-button technician-forklifts-tabs-button" onClick={() => setActiveTab('FORKLIFTS')}>
                  <span className="technician-forklifts-tabs-label">Forklifts</span>
                  <span className="technician-forklifts-tabs-size-label" aria-hidden="true">Active Forklifts</span>
                </button>
                <button type="button" role="tab" aria-selected={activeTab === 'DRAFTS'} className="fleet-toggle-button technician-forklifts-tabs-button" onClick={() => setActiveTab('DRAFTS')}>
                  <span className="technician-forklifts-tabs-label">Drafts</span>
                  <span className="technician-forklifts-tabs-size-label" aria-hidden="true">Retired Forklifts</span>
                </button>
              </div>
            </div>
          </header>
          {activeTab === 'FORKLIFTS' && (<>
            <Space
              className="dashboard-fleet-table-toolbar maintenance-list-toolbar maintenance-list-toolbar-no-helper"
              align="center"
              wrap
            >
              <Space className="dashboard-fleet-table-controls" wrap>
                <DashboardSearchInput
                  placeholder="Search forklifts"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  ariaLabel="Search forklifts"
                  className="dashboard-fleet-search maintenance-list-search"
                />
                <DashboardRefreshButton
                  onClick={loadForklifts}
                  loading={loading}
                  className="maintenance-list-refresh"
                />
              </Space>
            </Space>
            <div className="forklifts-page-table-wrap shared-dashboard-table"><Table rowKey="id" dataSource={filteredForklifts} columns={columns} pagination={false} size="middle" rowClassName={rowClassName} onRow={(record) => ({ onClick: () => setSelectedForkliftId(record.id) })} scroll={{ x: 'max-content' }} locale={{ emptyText: 'No forklifts found.' }} /></div>
            <div className="forklifts-page-mobile-list">
              {filteredForklifts.length ? filteredForklifts.map((forklift) => {
                const makeModel = joinParts([forklift.make, forklift.model]);
                const serial = valueOrBlank(forklift.serialNumber);
                const hours = valueOrBlank(forklift.hours);
                const metadata = [serial ? `Serial ${serial}` : '', hours || hours === 0 ? `${hours} hrs` : '']
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <article
                    key={forklift.id}
                    className={`maintenance-forklift-mobile-card shared-selected-card${forklift.id === selectedForkliftId ? ' is-selected' : ''}`}
                    onClick={() => setSelectedForkliftId(forklift.id)}
                  >
                    <div className="maintenance-forklift-mobile-card-header">
                      <span className="maintenance-forklift-mobile-unit">{valueOrBlank(forklift.unitId) || 'Unit unavailable'}</span>
                      <span className="maintenance-forklift-mobile-badges">
                        <ServiceStatusTag status={forklift.serviceStatus} />
                        <MaintenanceStatusTag status={forklift.maintenanceStatus} />
                      </span>
                    </div>
                    <div className="maintenance-forklift-mobile-details">
                      <div className="maintenance-forklift-mobile-model">{makeModel}</div>
                      <div className="maintenance-forklift-mobile-meta">{metadata}</div>
                    </div>
                    <div className="forklifts-page-mobile-actions technician-forklifts-mobile-actions">
                      <DashboardPrimaryActionButton onClick={(e) => { e.stopPropagation(); handleStartMaintenance(forklift); }}>Maintenance</DashboardPrimaryActionButton>
                      <Button className="technician-forklifts-mobile-secondary-action" onClick={(e) => { e.stopPropagation(); handleStartRepair(forklift); }}>Repair</Button>
                    </div>
                  </article>
                );
              }) : <Empty description="No forklifts found." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          </>)}
          {activeTab === 'DRAFTS' && <DraftsPage />}
        </DashboardPanel>
      </Spin>
    </DashboardPageShell>
  );
}
